/**
 * The title screen, the pause menu, and the two pages they share.
 *
 * One overlay with four pages: title (Continue, New Game, Settings), pause
 * (Resume, Settings, Controls, Quit to title), settings, and controls. It took
 * over from the click-to-play veil, and kept the veil's one job: pointer lock
 * can only be asked for from a real click, so Continue and Resume are clicks.
 * A pad does not need the lock, so A on the same button simply plays.
 *
 * It is not a modal in the game's sense. A modal stops the world from inside
 * the frame; this stops the loop from outside it (`main.js` does not step a
 * paused game), so a headless run that drives `step` itself never meets it.
 */
import { DEFAULT_SETTINGS, SETTING_RANGES } from '../core/settings.js';
import { currentDevice } from './glyphs.js';

const PAD_DIAGRAM = `
<svg class="pad-diagram" viewBox="0 0 420 230" role="img" aria-label="Controller layout">
  <path class="pad-body" d="M90 40 Q210 18 330 40 Q392 52 404 128 Q414 198 362 206 Q326 210 300 170 L120 170 Q94 210 58 206 Q6 198 16 128 Q28 52 90 40 Z"/>
  <circle class="pad-stick" cx="118" cy="92" r="20"/>
  <circle class="pad-stick" cx="260" cy="140" r="20"/>
  <g class="pad-dpad"><rect x="148" y="118" width="16" height="44" rx="3"/><rect x="134" y="132" width="44" height="16" rx="3"/></g>
  <circle class="pad-face" cx="314" cy="112" r="11"/><text x="314" y="116">A</text>
  <circle class="pad-face" cx="336" cy="90" r="11"/><text x="336" y="94">B</text>
  <circle class="pad-face" cx="292" cy="90" r="11"/><text x="292" y="94">X</text>
  <circle class="pad-face" cx="314" cy="68" r="11"/><text x="314" y="72">Y</text>
  <rect class="pad-small" x="186" y="76" width="16" height="10" rx="4"/>
  <rect class="pad-small" x="218" y="76" width="16" height="10" rx="4"/>
</svg>`;

export const PAD_ROWS = [
  ['Left stick', 'move  ·  click to run'],
  ['Right stick', 'look'],
  ['A', 'do what the prompt says  ·  hold for the wheel, stick to choose'],
  ['B', 'back out, close a panel'],
  ['X', 'hold to wind up, let go to throw'],
  ['Y', 'jump'],
  ['LB (hold)', 'read the sheet; A turns it over'],
  ['RB', 'file the room once it is set'],
  ['RT', 'on foot: set what you carry at the nearest spot on the plan'],
  ['RT  /  LT', 'drive: gas  /  brake and reverse'],
  ['D-pad', 'move through a menu  ·  up: route line  ·  down: what do I do?  ·  left and right: time scale'],
  ['View', 'controls card'],
  ['Menu', 'pause'],
];

export function createMenus({
  parent,
  settings,
  keyboardControls = [],
  hasSave = () => false,
  onContinue,
  onNewGame,
  onResume,
  onHelp,
  onQuit,
}) {
  const root = document.createElement('div');
  root.className = 'menus';
  root.hidden = true;
  parent.append(root);

  let screen = null;
  let page = null;
  let returnTo = null;
  let confirming = false;

  const slider = (name, label, [lo, hi], step) => `
    <label class="setting">
      <span>${label}</span>
      <input type="range" data-setting="${name}" min="${lo}" max="${hi}" step="${step}" value="${settings.get(name)}">
      <output data-for="${name}"></output>
    </label>`;

  function render() {
    const save = hasSave();
    const pages = {
      title: `
        <div class="menu-card menu-title">
          <span class="menu-kicker">Banquets  ·  Back of house</span>
          <h1>The Lion &amp; Crown</h1>
          <p class="menu-blurb">A resort at the edge of the desert, a new hire in Banquets, and a building that notices everything.</p>
          <div class="menu-buttons">
            ${save ? '<button type="button" data-do="continue" data-pad-default>Continue</button>' : ''}
            <button type="button" data-do="new" ${save ? '' : 'data-pad-default'}>${confirming ? 'Wipe the save and start a new week?' : 'New Game'}</button>
            <button type="button" data-do="settings">Settings</button>
          </div>
          <p class="menu-foot">${currentDevice() === 'gamepad' ? 'D-pad to move, A to choose' : 'Click to choose'}</p>
        </div>`,
      pause: `
        <div class="menu-card">
          <span class="menu-kicker">Paused</span>
          <h2>The clock has stopped</h2>
          <div class="menu-buttons">
            <button type="button" data-do="resume" data-pad-default>Resume</button>
            <button type="button" data-do="help">What do I do?</button>
            <button type="button" data-do="settings">Settings</button>
            <button type="button" data-do="controls">Controls</button>
            <button type="button" data-do="quit">Quit to title</button>
          </div>
        </div>`,
      settings: `
        <div class="menu-card menu-wide">
          <span class="menu-kicker">Settings</span>
          <h2>Sound and controls</h2>
          <div class="settings-grid">
            <div class="settings-col">
              ${slider('master', 'Master volume', SETTING_RANGES.master, 0.05)}
              ${slider('music', 'Music', SETTING_RANGES.music, 0.05)}
              ${slider('effects', 'Effects', SETTING_RANGES.effects, 0.05)}
              ${slider('sensitivity', 'Camera sensitivity', SETTING_RANGES.sensitivity, 0.05)}
              <label class="setting setting-check">
                <span>Invert camera Y</span>
                <input type="checkbox" data-setting="invertY" ${settings.get('invertY') ? 'checked' : ''}>
              </label>
              <label class="setting setting-check">
                <span>Route line to the objective</span>
                <input type="checkbox" data-setting="routeLine" ${settings.get('routeLine') ? 'checked' : ''}>
              </label>
              <button type="button" data-do="defaults">Reset to defaults</button>
            </div>
            <div class="settings-col">
              <span class="menu-label">Controller</span>
              ${PAD_DIAGRAM}
              <dl class="menu-keys">${PAD_ROWS.map(([k, v]) => `<dt>${k}</dt><dd>${v}</dd>`).join('')}</dl>
            </div>
          </div>
          <div class="menu-buttons menu-row"><button type="button" data-do="back" data-pad-default>Back</button></div>
        </div>`,
      controls: `
        <div class="menu-card menu-wide">
          <span class="menu-kicker">Controls</span>
          <h2>Keyboard and controller</h2>
          <div class="settings-grid">
            <div class="settings-col">
              <span class="menu-label">Keyboard and mouse</span>
              <dl class="menu-keys">${keyboardControls.map(([k, v]) => `<dt>${k}</dt><dd>${v}</dd>`).join('')}</dl>
            </div>
            <div class="settings-col">
              <span class="menu-label">Controller</span>
              <dl class="menu-keys">${PAD_ROWS.map(([k, v]) => `<dt>${k}</dt><dd>${v}</dd>`).join('')}</dl>
            </div>
          </div>
          <div class="menu-buttons menu-row"><button type="button" data-do="back" data-pad-default>Back</button></div>
        </div>`,
    };
    root.innerHTML = pages[page] ?? '';
    root.dataset.page = page ?? '';
    for (const out of root.querySelectorAll('output[data-for]')) {
      out.textContent = shown(out.dataset.for, settings.get(out.dataset.for));
    }
  }

  const shown = (name, v) => (name === 'sensitivity' ? `${Number(v).toFixed(2)}x` : `${Math.round(Number(v) * 100)}%`);

  function go(next) {
    if (next === 'settings' || next === 'controls') returnTo = page;
    page = next;
    confirming = false;
    render();
  }

  root.addEventListener('input', (event) => {
    const input = event.target.closest('[data-setting]');
    if (!input || input.type !== 'range') return;
    settings.set(input.dataset.setting, Number(input.value));
    const out = root.querySelector(`output[data-for="${input.dataset.setting}"]`);
    if (out) out.textContent = shown(input.dataset.setting, settings.get(input.dataset.setting));
  });
  root.addEventListener('change', (event) => {
    const input = event.target.closest('[data-setting]');
    if (!input || input.type !== 'checkbox') return;
    settings.set(input.dataset.setting, input.checked);
  });

  root.addEventListener('click', (event) => {
    const button = event.target.closest('[data-do]');
    if (!button) return;
    const what = button.dataset.do;
    if (what === 'continue') onContinue?.();
    else if (what === 'resume') onResume?.();
    else if (what === 'help') onHelp?.();
    else if (what === 'new') {
      if (hasSave() && !confirming) {
        confirming = true;
        render();
        root.querySelector('[data-do="new"]')?.classList.add('pad-focus');
        return;
      }
      onNewGame?.();
    } else if (what === 'settings') go('settings');
    else if (what === 'controls') go('controls');
    else if (what === 'back') back();
    else if (what === 'quit') onQuit?.();
    else if (what === 'defaults') {
      for (const [name, v] of Object.entries(DEFAULT_SETTINGS)) settings.set(name, v);
      render();
    }
  });

  function back() {
    if (page === 'settings' || page === 'controls') {
      go(returnTo ?? screen);
      return true;
    }
    if (page === 'pause') {
      onResume?.();
      return true;
    }
    return false;
  }

  return {
    root,
    get open() {
      return !root.hidden;
    },
    get screen() {
      return root.hidden ? null : screen;
    },
    get page() {
      return root.hidden ? null : page;
    },
    showTitle() {
      screen = 'title';
      page = 'title';
      returnTo = null;
      confirming = false;
      render();
      root.hidden = false;
    },
    showPause() {
      screen = 'pause';
      page = 'pause';
      returnTo = null;
      render();
      root.hidden = false;
    },
    hide() {
      root.hidden = true;
      screen = null;
      page = null;
    },
    back,
    /** Redraws, for a device change that renames a key on the title. */
    refresh() {
      if (!root.hidden) render();
    },
  };
}
